import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFundedSummary } from '../../hooks/useFundedSummary';
import type { ColDef } from 'ag-grid-community';
import { toast as message } from 'sonner';
import { supabaseClient } from '../../utils/supabase';
import { startDateWithTenure, tenureLabel } from '../../utils/dateFormat';
import { useMyCapabilities } from '../../utils/metadata/capabilities';
import FundedDashboard from '../admin/reporting/FundedDashboard';
import { useRecordNavigator, useRecordParam } from '../../hooks/useRecordNavigator';
import { useFillGridHeight } from '../../hooks/useFillGridHeight';
import { useTableActiveFilter, useTableZoom } from '../../utils/tableViewSettings';
import type { ActiveFilterValue } from '../../utils/tableViewSettings';
import { GridZoomControl } from '../ui/GridZoomControl';
import { getEmailValidationError, getPhoneValidationError } from '../../utils/validation';
import type { GridRowAction } from '../GridRowActions/GridRowActions';
import { useGlobalMetadata } from '../../utils/providers';
import { prefetchRecordDestinationIfAbsent } from '../../utils/recordDestinationPrefetch';
import WorkspaceGrid from '../WorkspaceGrid/WorkspaceGrid';
import { useTableInlineEdit } from '../WorkspaceGrid/useTableInlineEdit';
import { EditModeToggle, UndoLastEditButton } from '../TableGrid/EditModeToggle';
import SendOfferDialog from '../SendOffer/SendOfferDialog';
import SendFundedDialog from '../SendOffer/SendFundedDialog';
import { ListLeaderboard } from '../ListLeaderboard/ListLeaderboard';
import CreateListRecordButton from '../CreateListRecordButton';
import EnhancedDialog from '../EnhancedDialog';
import { ValidatedEmailInput } from '../ValidatedEmailInput';
import { ValidatedPhoneInput } from '../ValidatedPhoneInput';
import { ApprovalTextChip } from './ApprovalTextChip';
import { LivePicklistCell, MERCHANT_FEEDBACK_EDIT } from '../ReportPage/LivePicklistCell';
import { queryClient } from '../../utils/constants';
import RepLoginLinkDialog from '../admin/RepLoginLinkDialog';
import './RepPage.css';
import { SHEET_DEAL_SELECT, sheetDealColumns, sheetDealFields, type SheetDealFields } from '../../utils/sheetDealColumns';

type JsonObject = Record<string, unknown>;

export interface RepRecord {
  id: number;
  name: string;
  phone: string;
  /** Display email: the rep's own contact email, falling back to the linked login email. */
  email: string;
  /** The editable rep.email column value (may be empty when only a login email exists). */
  contactEmail: string;
  /** The CRM login linked to this rep (rep.app_user); Openers and Closers see their deals through it. */
  loginId: number | null;
  loginLabel: string;
  /** REG-876: rep.default_additional_rep, linked (visibility only) on every new merchant of this rep. */
  defaultAdditionalRepId: number | null;
  /** REG-859: rep.closer, the closer assigned to this opener (public.rep_closer_of). */
  closerId: number | null;
  createdAt: string | null;
  active: boolean;
  merchantCount: number;
  submissionCount: number;
  fundedCount: number;
  fundedAmount: number;
  grossCommission: number;
  approvalRate: number;
  /** Submissions in an approved, contracts or funded status. */
  approvedCount: number;
  /** REG-828: funded submissions over approved submissions, as a percentage. */
  approvalToFundedPct: number | null;
  /** REG-828: amount of the submissions counted in approvedCount. */
  approvedAmount: number;
  /** REG-828: merchants with an approved (or funded) submission over all the rep's merchants, as a percentage. */
  merchantsToApprovalsPct: number | null;
  /** REG-828: merchants with a funded submission over all the rep's merchants, as a percentage. */
  merchantsToFundedPct: number | null;
  funderCount: number;
  lastSubmissionAt: string | null;
}

interface SubmissionRecord extends SheetDealFields {
  id: number;
  repId: number;
  merchantId: number | null;
  merchant: string;
  funder: string;
  status: string;
  decision: Decision;
  amount: number;
  factorRate: number | null;
  term: number | null;
  declineReason: string;
  reason: string;
  date: string | null;
  timestamp: number;
  dateFunded: string | null;
  approvalDate: string | null;
  /** This submission is its merchant's canonical primary approval. */
  isPrimary: boolean;
  /** Merchant contact + deal fields feeding the Send row actions (REG-663). */
  merchantEmail: string;
  merchantFirstName: string;
  structure: string | null;
  mainRep: number | null;
  /** merchant.merchant_response, set from the Approvals tab's live dropdown (REG-740). */
  merchantResponse: string | null;
}

type Decision = 'funded' | 'approved' | 'declined' | 'no_response';

const APPROVED_STATUSES = new Set(['approved', 'contracts out', 'contracts in']);
// The owners work deals under rep records too, but stay off the Top Reps
// leaderboard (REG-658). Matched by name: rep ids differ between the dev and
// prod databases, names do not.
const OWNER_REP_NAMES = new Set(['zack wolf', 'david vingart']);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const AVATAR_COLORS = ['#4cb8d4', '#6366f1', '#059669', '#d97706', '#8b5cf6', '#0f766e'];
const AMOUNT_BUCKETS = [
  { value: 'all', label: 'Any amount', min: 0, max: Number.POSITIVE_INFINITY },
  { value: 'under25', label: '< $25k', min: 0, max: 25_000 },
  { value: '25to50', label: '$25k - $50k', min: 25_000, max: 50_000 },
  { value: '50to100', label: '$50k - $100k', min: 50_000, max: 100_000 },
  { value: 'over100', label: '> $100k', min: 100_000, max: Number.POSITIVE_INFINITY },
];

function jsonObject(value: unknown): JsonObject {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : {};
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function decisionFor(status: string): Decision {
  const normalized = status.trim().toLowerCase();
  if (normalized === 'funded') return 'funded';
  if (APPROVED_STATUSES.has(normalized)) return 'approved';
  if (normalized === 'declined') return 'declined';
  return 'no_response';
}

export async function fetchRepSubmissions(repId: number): Promise<SubmissionRecord[]> {
  const supabase = supabaseClient as any;
  const pageSize = 1000;
  const rows: any[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .from('rep_submission_activity_v')
      .select(`rep,submission,merchant,merchant_label,funder_label,status,amount,factor_rate,term,reason_for_decline,missing_documents,activity_date,date_funded,approval_date,is_primary_approval,merchant_email,merchant_first_name,structure,main_rep,merchant_response,${SHEET_DEAL_SELECT}`)
      .eq('rep', repId)
      .order('submission', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    rows.push(...(data ?? []));
    if (!data || data.length < pageSize) break;
  }
  return mapSubmissionRows(rows);
}

function mapSubmissionRows(submissionRows: any[]): SubmissionRecord[] {
  return submissionRows.flatMap((row): SubmissionRecord[] => {
    if (row.submission == null || row.rep == null) return [];
    const date = row.activity_date;
    const declineReason = row.reason_for_decline || '';
    const reason = declineReason || row.missing_documents || '';
    return [{
      id: row.submission, repId: row.rep, merchantId: row.merchant,
      merchant: row.merchant_label || `Merchant #${row.merchant ?? '-'}`,
      funder: row.funder_label || 'Unassigned', status: row.status || 'No Response',
      decision: decisionFor(row.status || ''), amount: Number(row.amount) || 0,
      factorRate: row.factor_rate, term: row.term, declineReason, reason, date,
      timestamp: date ? new Date(date).getTime() : 0,
      dateFunded: row.date_funded ?? null,
      approvalDate: row.approval_date ?? null,
      isPrimary: row.is_primary_approval === true,
      merchantEmail: row.merchant_email || '',
      merchantFirstName: row.merchant_first_name || '',
      structure: row.structure ?? null,
      mainRep: row.main_rep ?? null,
      merchantResponse: row.merchant_response ?? null,
      ...sheetDealFields(row),
    }];
  }).sort((a, b) => b.timestamp - a.timestamp);
}

export async function loadRepPageData(): Promise<RepRecord[]> {
  const supabase = supabaseClient as any;
  const [repResult, performanceResult] = await Promise.all([
    // Cast: rep.email is new and the generated types have not been refreshed.
    supabase
      .from('rep_deep_view')
      .select('id,name,phone,email,is_active,created_at,app_user_rel,default_additional_rep,closer')
      .order('name', { ascending: true }),
    supabase.from('rep_performance_summary_v').select('*'),
  ]);
  if (repResult.error) throw repResult.error;
  if (performanceResult.error) throw performanceResult.error;
  const performanceByRep = new Map<number, any>((performanceResult.data ?? []).map((row: any) => [Number(row.rep), row]));

  const reps = (repResult.data ?? []).flatMap((row: any) => {
    if (row.id == null) return [];
    const user = jsonObject(row.app_user_rel);
    const performance = performanceByRep.get(row.id) ?? {};
    const contactEmail = stringValue(row.email);
    return [{
      id: row.id,
      name: row.name || `Rep #${row.id}`,
      phone: row.phone || '',
      email: contactEmail || stringValue(user.email),
      contactEmail,
      loginId: user.id != null ? Number(user.id) : null,
      loginLabel: stringValue(user.name) || stringValue(user.email),
      defaultAdditionalRepId: row.default_additional_rep != null ? Number(row.default_additional_rep) : null,
      closerId: row.closer != null ? Number(row.closer) : null,
      createdAt: row.created_at,
      active: row.is_active !== false,
      merchantCount: Number(performance.merchant_count) || 0,
      submissionCount: Number(performance.submission_count) || 0,
      fundedCount: Number(performance.funded_count) || 0,
      fundedAmount: Number(performance.funded_amount) || 0,
      grossCommission: Number(performance.gross_commission) || 0,
      approvalRate: Number(performance.approval_rate) || 0,
      approvedCount: Number(performance.approved_count) || 0,
      approvalToFundedPct: Number(performance.approved_count) > 0
        ? Math.round((1000 * (Number(performance.funded_count) || 0)) / Number(performance.approved_count)) / 10
        : null,
      approvedAmount: Number(performance.approved_amount) || 0,
      merchantsToApprovalsPct: Number(performance.merchant_count) > 0
        ? Math.round((1000 * (Number(performance.approved_merchant_count) || 0)) / Number(performance.merchant_count)) / 10
        : null,
      merchantsToFundedPct: Number(performance.merchant_count) > 0
        ? Math.round((1000 * (Number(performance.funded_merchant_count) || 0)) / Number(performance.merchant_count)) / 10
        : null,
      funderCount: Number(performance.funder_count) || 0,
      lastSubmissionAt: performance.last_submission_at || null,
    }];
  });
  return reps;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
}

function money(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function shortMoney(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return money(value);
}

function shortDate(value: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }).format(new Date(value));
}

function tenure(value: string | null): string {
  return tenureLabel(value) || '-';
}

// One-per-merchant stat basis (REG-646, mirrors the main dashboard): primary
// approvals come from the canonical merchant.primary_approval flag, and both
// rates divide per-merchant counts into the per-merchant applications
// denominator. Funded units/amount/avg stay per funded submission.
function statsFor(submissions: SubmissionRecord[]) {
  const funded = submissions.filter((row) => row.decision === 'funded');
  const primary = submissions.filter((row) => row.isPrimary);
  const merchantsWithoutId = submissions.filter((row) => row.merchantId == null).length;
  const applications = new Set(submissions.map((row) => row.merchantId).filter(Boolean)).size + merchantsWithoutId;
  const fundedMerchants = new Set(funded.map((row) => row.merchantId).filter(Boolean)).size
    + funded.filter((row) => row.merchantId == null).length;
  const fundedAmount = funded.reduce((sum, row) => sum + row.amount, 0);
  const approvalAmount = primary.reduce((sum, row) => sum + row.amount, 0);
  // Approved units (REG-651): one per merchant — any merchant with an
  // approval or a funded deal counts as approved. The Funded Rate is the
  // approved-to-funded conversion: funded units over approved units.
  const approvedUnits = new Set(
    submissions
      .filter((row) => row.isPrimary || row.decision === 'approved' || row.decision === 'funded')
      .map((row) => row.merchantId ?? `s${row.id}`),
  ).size;
  return {
    total: submissions.length,
    primaryApprovals: primary.length,
    applications,
    approvalAmount,
    approvedUnits,
    fundedUnits: funded.length,
    fundedMerchants,
    fundedAmount,
    averageDeal: funded.length ? Math.round(fundedAmount / funded.length) : 0,
    approvalRate: applications ? Math.round((approvedUnits / applications) * 100) : 0,
    fundedRate: approvedUnits ? Math.min(100, Math.round((funded.length / approvedUnits) * 100)) : 0,
    lastDate: submissions[0]?.date ?? null,
  };
}

type MonthMetric = 'primary' | 'applications' | 'approvalAmount' | 'fundedUnits' | 'fundedAmount';

function monthKey(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : `${date.getFullYear()}-${date.getMonth()}`;
}

// Month-over-month deltas are fixed facts about the current vs previous
// calendar month, computed from ALL of the rep's submissions so they do not
// shift as page filters change.
function monthValue(submissions: SubmissionRecord[], metric: MonthMetric, key: string): number {
  switch (metric) {
    case 'primary':
      return submissions.filter((row) => row.isPrimary && monthKey(row.approvalDate ?? row.date) === key).length;
    case 'approvalAmount':
      return submissions.filter((row) => row.isPrimary && monthKey(row.approvalDate ?? row.date) === key)
        .reduce((sum, row) => sum + row.amount, 0);
    case 'fundedUnits':
      return submissions.filter((row) => monthKey(row.dateFunded) === key).length;
    case 'fundedAmount':
      return submissions.filter((row) => monthKey(row.dateFunded) === key)
        .reduce((sum, row) => sum + row.amount, 0);
    case 'applications': {
      // An application counts in the month its merchant first appears.
      const firstByMerchant = new Map<number | string, number>();
      for (const row of submissions) {
        const id = row.merchantId ?? `s${row.id}`;
        const existing = firstByMerchant.get(id);
        if (existing == null || row.timestamp < existing) firstByMerchant.set(id, row.timestamp);
      }
      return [...firstByMerchant.values()]
        .filter((ts) => ts > 0 && monthKey(new Date(ts).toISOString()) === key).length;
    }
  }
}

export interface MonthDelta { pct: number | null; up: boolean; title: string }

function monthDelta(submissions: SubmissionRecord[], metric: MonthMetric, money?: boolean): MonthDelta {
  const now = new Date();
  const thisKey = `${now.getFullYear()}-${now.getMonth()}`;
  const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastKey = `${previous.getFullYear()}-${previous.getMonth()}`;
  const current = monthValue(submissions, metric, thisKey);
  const last = monthValue(submissions, metric, lastKey);
  const format = (value: number) => (money ? shortMoney(value) : String(value));
  const thisLabel = MONTHS[now.getMonth()];
  const lastLabel = MONTHS[previous.getMonth()];
  const title = `${thisLabel}: ${format(current)} · ${lastLabel}: ${format(last)}`;
  if (last === 0) return { pct: null, up: current > 0, title };
  return { pct: Math.round(((current - last) / last) * 100), up: current >= last, title };
}

function LoadingState({ error }: { error?: Error | null }) {
  return (
    <div className="rep-page-state">
      {error ? <><strong>Unable to load reps</strong><span>{error.message}</span></> : <><span className="rep-spinner" /><span>Loading rep performance...</span></>}
    </div>
  );
}

function RepContactDialog({ rep, onClose, onSaved }: { rep: RepRecord; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const [email, setEmail] = useState(rep.contactEmail || rep.email);
  const [phone, setPhone] = useState(rep.phone);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const validationError = getEmailValidationError(email) || getPhoneValidationError(phone);
    if (validationError) {
      message.error(validationError);
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await (supabaseClient as any)
        .from('rep')
        .update({ email: email.trim() || null, phone: phone.trim() || null })
        .eq('id', rep.id)
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('You may not have permission to edit rep contact info.');
      await onSaved();
      message.success('Contact info updated.');
      onClose();
    } catch (error) {
      message.error((error as Error).message || 'Unable to save contact info.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EnhancedDialog isOpen onClose={onClose} title={`Edit contact info — ${rep.name}`} canOutsideClickClose={!saving}>
      <div className="sky-dialog-body rep-contact-dialog">
        <label className="rep-contact-field">
          Email
          <ValidatedEmailInput value={email} onChange={setEmail} onValidChange={() => {}} disabled={saving} />
        </label>
        <label className="rep-contact-field">
          Phone
          <ValidatedPhoneInput value={phone} onChange={setPhone} onValidChange={() => {}} disabled={saving} />
        </label>
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="sky-btn sky-btn-primary" disabled={saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </EnhancedDialog>
  );
}

/**
 * REG-876: pick the rep's default additional rep (Jonathan Fox on new reps).
 * Saving only changes the setting for this rep's future merchants; existing
 * merchants keep the additional reps they already have.
 */
function RepDefaultAdditionalRepDialog({ rep, reps, onClose, onSaved }: { rep: RepRecord; reps: RepRecord[]; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const [value, setValue] = useState(rep.defaultAdditionalRepId != null ? String(rep.defaultAdditionalRepId) : '');
  const [saving, setSaving] = useState(false);
  const options = reps.filter((other) => other.id !== rep.id && (other.active || other.id === rep.defaultAdditionalRepId));

  const save = async () => {
    setSaving(true);
    try {
      const { data, error } = await (supabaseClient as any)
        .from('rep')
        .update({ default_additional_rep: value ? Number(value) : null })
        .eq('id', rep.id)
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('You may not have permission to edit this rep.');
      await onSaved();
      message.success(value ? 'Default additional rep updated.' : 'Default additional rep cleared.');
      onClose();
    } catch (error) {
      message.error((error as Error).message || 'Unable to save the default additional rep.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EnhancedDialog isOpen onClose={onClose} title={`Default additional rep - ${rep.name}`} canOutsideClickClose={!saving}>
      <div className="sky-dialog-body rep-contact-dialog">
        <label className="rep-contact-field">
          Default additional rep
          <select
            aria-label="Default additional rep"
            value={value}
            disabled={saving}
            onChange={(event) => setValue(event.target.value)}
          >
            <option value="">None</option>
            {options.map((other) => <option key={other.id} value={other.id}>{other.name}</option>)}
          </select>
        </label>
        <p className="rep-contact-hint">
          Added as an additional rep (visibility only, no commission) on every new merchant of {rep.name}.
          Existing merchants are not changed.
        </p>
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="sky-btn sky-btn-primary" disabled={saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </EnhancedDialog>
  );
}

/**
 * REG-859: pick the rep's closer (rep.closer). Deal prep, the submission funder
 * email cc and the funded-deal split read it through public.rep_closer_of.
 */
function RepCloserDialog({ rep, reps, onClose, onSaved }: { rep: RepRecord; reps: RepRecord[]; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const [value, setValue] = useState(rep.closerId != null ? String(rep.closerId) : '');
  const [saving, setSaving] = useState(false);
  const options = reps.filter((other) => other.id !== rep.id && (other.active || other.id === rep.closerId));

  const save = async () => {
    setSaving(true);
    try {
      const { data, error } = await (supabaseClient as any)
        .from('rep')
        .update({ closer: value ? Number(value) : null })
        .eq('id', rep.id)
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('You may not have permission to edit this rep.');
      await onSaved();
      message.success(value ? 'Closer updated.' : 'Closer cleared.');
      onClose();
    } catch (error) {
      message.error((error as Error).message || 'Unable to save the closer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EnhancedDialog isOpen onClose={onClose} title={`Closer - ${rep.name}`} canOutsideClickClose={!saving}>
      <div className="sky-dialog-body rep-contact-dialog">
        <label className="rep-contact-field">
          Closer
          <select
            aria-label="Closer"
            value={value}
            disabled={saving}
            onChange={(event) => setValue(event.target.value)}
          >
            <option value="">None</option>
            {options.map((other) => <option key={other.id} value={other.id}>{other.name}</option>)}
          </select>
        </label>
        <p className="rep-contact-hint">
          The closer assigned to {rep.name}: copied on their deal preps and funder emails, and added as an additional rep when their deals fund.
        </p>
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="sky-btn sky-btn-primary" disabled={saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </EnhancedDialog>
  );
}

interface RepListProps {
  reps: RepRecord[];
  onOpen: (id: number) => void;
  createAction?: React.ReactNode;
  onEditContact?: (rep: RepRecord) => void;
  onToggleActive?: (rep: RepRecord, active: boolean) => Promise<void>;
}

function RepList({ reps, onOpen, createAction, onEditContact, onToggleActive }: RepListProps) {
  const [search, setSearch] = useState('');
  // Per-table zoom, remembered across sessions (REG-653).
  const [tableZoom, setTableZoom] = useTableZoom('rep');
  // Active/Inactive/All choice remembered across sessions (REG-673).
  const [activeFilter, setActiveFilter] = useTableActiveFilter('rep');
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const query = search.trim().toLowerCase();
  const handleToggle = async (rep: RepRecord, active: boolean) => {
    if (!onToggleActive || togglingId != null) return;
    setTogglingId(rep.id);
    try {
      await onToggleActive(rep, active);
      message.success(active ? `${rep.name} marked active.` : `${rep.name} marked inactive.`);
    } catch (error) {
      message.error((error as Error).message || 'Unable to update active status.');
    } finally {
      setTogglingId(null);
    }
  };
  const columns = useMemo<ColDef<RepRecord>[]>(() => [
    ...REP_COLUMNS,
    { headerName: 'ACTIVE', colId: 'active', width: 100, filter: 'agSetColumnFilter',
      cellClass: 'rep-active-cell',
      valueGetter: (p) => (p.data?.active ? 'Yes' : 'No'),
      cellRenderer: (p: { data?: RepRecord }) => p.data ? (
        <input
          type="checkbox"
          className="rep-active-checkbox"
          aria-label={`${p.data.name} active`}
          checked={p.data.active}
          disabled={!onToggleActive || togglingId === p.data.id}
          onChange={(event) => void handleToggle(p.data!, event.target.checked)}
        />
      ) : null },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [onToggleActive, togglingId]);
  const inlineEdit = useTableInlineEdit<RepRecord>({ tableName: 'rep', columns, invalidateQueryKeys: REP_LIST_KEYS });
  const rowActions = useMemo<GridRowAction<RepRecord>[] | undefined>(() => (
    onEditContact ? [{
      id: 'edit-rep-contact',
      label: 'Edit',
      variant: 'button',
      ariaLabel: (rep) => `Edit contact info for ${rep.name}`,
      onSelect: onEditContact,
    }] : undefined
  ), [onEditContact]);
  const rows = reps.filter((rep) => {
    const matchesActivity = activeFilter === 'all'
      || (activeFilter === 'active' ? rep.active : !rep.active);
    return matchesActivity && `${rep.name} ${rep.email} ${rep.phone}`.toLowerCase().includes(query);
  });
  // Leaderboard follows the active filter but not the search, and never
  // includes the owners (REG-658).
  const leaderboardEntries = reps
    .filter((rep) => (activeFilter === 'all' || (activeFilter === 'active' ? rep.active : !rep.active))
      && !OWNER_REP_NAMES.has(rep.name.trim().toLowerCase()))
    .map((rep) => ({
      id: rep.id,
      name: rep.name,
      rank: rep.fundedCount,
      tieBreak: rep.fundedAmount,
      stats: [
        { label: 'Units', value: String(rep.fundedCount) },
        { label: 'Funded', value: shortMoney(rep.fundedAmount), accent: 'var(--green)' },
        { label: 'Gross', value: shortMoney(rep.grossCommission), accent: 'var(--accent)' },
      ],
    }));

  // Mini stats dashboard above the leaderboard + grid (REG-661/662). The
  // header shows the CANONICAL unique funded totals (same source as the
  // Funded dashboard) — summing per-rep rows counts a submission once per
  // linked rep and wildly overstates the book.
  const fundedSummary = useFundedSummary().data;
  const activeCount = reps.filter((rep) => rep.active).length;

  return (
    <>
    <div className="rep-stat-grid primary">
      <StatCard large label="Amount Funded" value={fundedSummary ? shortMoney(fundedSummary.amount_funded) : '—'} sub={fundedSummary ? money(fundedSummary.amount_funded) : 'unique funded deals'} accent="var(--green)" />
      <StatCard large label="Units Funded" value={fundedSummary ? fundedSummary.deals_funded.toLocaleString() : '—'} sub="unique funded deals" />
      <StatCard large label="Gross Commission" value={fundedSummary ? shortMoney(fundedSummary.total_commission) : '—'} sub={fundedSummary ? money(fundedSummary.total_commission) : 'unique funded deals'} accent="var(--accent)" />
      <StatCard large label="Average Deal" value={fundedSummary ? shortMoney(fundedSummary.average_deal) : '—'} sub="per funded deal" />
      <StatCard large label="Active Reps" value={activeCount.toLocaleString()} sub={`of ${reps.length} total`} />
    </div>
    <ListLeaderboard
      title="Top Reps"
      subtitle={`by funded units · ${activeFilter === 'all' ? 'all reps' : `${activeFilter} reps`} · owners excluded`}
      entries={leaderboardEntries}
      onOpen={onOpen}
    />
    <section className="rep-list-card" style={{ ['--sky-grid-zoom' as string]: tableZoom }}>
      <div className="rep-list-toolbar">
          <label className="rep-search">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search reps..." />
          </label>
        <select
          className="rep-active-filter"
          aria-label="Filter reps by active status"
          value={activeFilter}
          onChange={(event) => setActiveFilter(event.target.value as ActiveFilterValue)}
        >
          <option value="active">Active reps</option>
          <option value="inactive">Inactive reps</option>
          <option value="all">All reps</option>
        </select>
        <span><b>{rows.length}</b> of {reps.length} reps</span>
        <GridZoomControl zoom={tableZoom} onChange={setTableZoom} />
        {inlineEdit.hasEditableColumns && (
          <>
            <UndoLastEditButton canUndo={inlineEdit.canUndo} nextUndoLabel={inlineEdit.nextUndoLabel} onClick={() => void inlineEdit.undoLast()} />
            <EditModeToggle active={inlineEdit.editMode} onClick={inlineEdit.toggleEditMode} />
          </>
        )}
        {createAction}
      </div>
      <WorkspaceGrid<RepRecord>
        exportName="Reps"
        {...inlineEdit.gridProps}
        rows={rows}
        columns={inlineEdit.columns}
        getRowId={(rep) => String(rep.id)}
        onRowClick={(rep) => onOpen(rep.id)}
        rowActions={rowActions}
        storageKey="rep_list"
        rowHeight={Math.round(52 * tableZoom)}
        emptyMessage="No reps match these filters."
      />
    </section>
    </>
  );
}

function Select({ value, onChange, children, label }: { value: string; onChange: (value: string) => void; children: React.ReactNode; label: string }) {
  return <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)}>{children}</select>;
}

function DeltaChip({ delta }: { delta: MonthDelta }) {
  const text = delta.pct == null ? (delta.up ? 'new' : '—') : `${delta.up ? '▲' : '▼'} ${Math.abs(delta.pct)}%`;
  const tone = delta.pct == null && !delta.up ? 'flat' : delta.up ? 'up' : 'down';
  return (
    <span className={`rep-stat-delta ${tone}`} title={`${delta.title} (this month vs last month)`}>
      {text}<em>since last month</em>
    </span>
  );
}

function StatCard({ label, value, sub, accent, large, delta }: { label: string; value: React.ReactNode; sub: string; accent?: string; large?: boolean; delta?: MonthDelta }) {
  return (
    <div className={`rep-stat ${large ? 'large' : ''}`}>
      <span className="rep-stat-label">{label}{delta && <DeltaChip delta={delta} />}</span>
      <b style={{ color: accent }}>{value}</b>
      <small>{sub}</small>
    </div>
  );
}

function RateCard({ label, value, sub, accent }: { label: string; value: number; sub: string; accent: string }) {
  return <div className="rep-rate"><div className="rep-rate-ring" style={{ background: `conic-gradient(${accent} ${value * 3.6}deg, var(--border-soft) 0)` }}><span>{value}%</span></div><div><span className="rep-stat-label">{label}</span><small>{sub}</small></div></div>;
}

interface FunderRollup { funder: string; fundedUnits: number; fundedAmount: number; submissions: number }

function funderRollups(submissions: SubmissionRecord[]): FunderRollup[] {
  const byFunder = new Map<string, FunderRollup>();
  for (const row of submissions) {
    if (row.funder === 'Unassigned') continue;
    const entry = byFunder.get(row.funder) ?? { funder: row.funder, fundedUnits: 0, fundedAmount: 0, submissions: 0 };
    entry.submissions += 1;
    if (row.decision === 'funded') {
      entry.fundedUnits += 1;
      entry.fundedAmount += row.amount;
    }
    byFunder.set(row.funder, entry);
  }
  return [...byFunder.values()].sort((a, b) =>
    b.fundedUnits - a.fundedUnits || b.fundedAmount - a.fundedAmount || b.submissions - a.submissions);
}

function FavoriteFunderCard({ favorite }: { favorite: FunderRollup | undefined }) {
  return (
    <div className="rep-stat rep-favorite-funder">
      <span className="rep-stat-label">Favorite Funder</span>
      {favorite ? (
        <>
          <b title={favorite.funder}>{favorite.funder}</b>
          <small>{favorite.fundedUnits} funded · {shortMoney(favorite.fundedAmount)} · {favorite.submissions} subs</small>
        </>
      ) : (
        <><b>-</b><small>no funder activity yet</small></>
      )}
    </div>
  );
}

/** Rep-scoped funded volume by month, last 12 months (funded date basis). */
function MonthlyFundedChart({ submissions }: { submissions: SubmissionRecord[] }) {
  const now = new Date();
  const buckets: Array<{ label: string; amount: number }> = [];
  for (let offset = 11; offset >= 0; offset -= 1) {
    const month = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const key = `${month.getFullYear()}-${month.getMonth()}`;
    const amount = submissions
      .filter((row) => row.decision === 'funded' && monthKey(row.dateFunded) === key)
      .reduce((sum, row) => sum + row.amount, 0);
    buckets.push({ label: `${MONTHS[month.getMonth()]}${month.getMonth() === 0 ? ` '${String(month.getFullYear()).slice(2)}` : ''}`, amount });
  }
  const peak = Math.max(...buckets.map((bucket) => bucket.amount), 1);
  return (
    <section className="rep-data-card">
      <div className="rep-card-title"><b>Funded Volume</b><span>by month · last 12 months</span></div>
      <div className="rep-month-chart">
        {buckets.map((bucket) => (
          <div key={bucket.label} className="rep-month-col" title={`${bucket.label}: ${money(bucket.amount)}`}>
            <small>{bucket.amount > 0 ? shortMoney(bucket.amount) : ''}</small>
            <div className="rep-month-bar" style={{ height: `${Math.max((bucket.amount / peak) * 100, bucket.amount > 0 ? 3 : 1)}%` }} />
            <span>{bucket.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Rep-scoped funder ranking by funded volume (mirrors the main dashboard). */
function TopFundersCard({ rollups }: { rollups: FunderRollup[] }) {
  const top = rollups.filter((entry) => entry.fundedAmount > 0)
    .sort((a, b) => b.fundedAmount - a.fundedAmount)
    .slice(0, 6);
  const peak = Math.max(...top.map((entry) => entry.fundedAmount), 1);
  return (
    <section className="rep-data-card">
      <div className="rep-card-title"><b>Top Funders</b><span>by funded volume</span></div>
      {top.length === 0 ? (
        <div className="rep-top-funders-empty">No funded deals yet.</div>
      ) : (
        <div className="rep-top-funders">
          {top.map((entry, index) => (
            <div key={entry.funder} className="rep-top-funder-row" title={`${entry.funder}: ${money(entry.fundedAmount)} · ${entry.fundedUnits} funded`}>
              <i>{index + 1}</i>
              <div>
                <div className="rep-top-funder-head"><b>{entry.funder}</b><em>{shortMoney(entry.fundedAmount)}</em></div>
                <div className="rep-top-funder-track"><span style={{ width: `${(entry.fundedAmount / peak) * 100}%` }} /></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const STATUS_LABELS: Record<Decision, string> = { funded: 'Funded', approved: 'Approved', declined: 'Declined', no_response: 'No Response' };

const REP_LIST_KEYS = [['rep-performance-page']];

// REG-828: default columns and order from the COLUMNS sheet (REP). Columns the
// sheet does not list stay available, hidden, in the column chooser.
const REP_COLUMNS: ColDef<RepRecord>[] = [
  { headerName: 'REP / GROUP', colId: 'name', field: 'name', width: 240, context: { editField: 'name' },
    cellRenderer: (p: { data?: RepRecord }) => p.data ? (
      <div className="rep-person">
        <span className="rep-avatar" style={{ background: AVATAR_COLORS[p.data.id % AVATAR_COLORS.length] }}>{initials(p.data.name)}</span>
        <span><b>{p.data.name}</b><small>{p.data.active ? 'Sales representative' : 'Inactive'}</small></span>
      </div>
    ) : null },
  { headerName: 'START DATE / TENURE', colId: 'createdAt', field: 'createdAt', width: 170,
    valueFormatter: (p) => startDateWithTenure(p.value) },
  // Shows the login's email when the rep has none of its own; edits write rep.email.
  { headerName: 'EMAIL', field: 'email', width: 220, cellClass: 'rep-email', valueFormatter: (p) => p.value || '-',
    context: { editField: 'email', rowField: 'contactEmail' } },
  { headerName: 'PHONE', field: 'phone', width: 150, valueFormatter: (p) => p.value || '-', context: { editField: 'phone' } },
  { headerName: 'APPLICATIONS', field: 'merchantCount', type: 'rightAligned', width: 135, filter: 'agNumberColumnFilter', cellClass: 'numeric' },
  { headerName: 'APPROVALS UNITS', field: 'approvedCount', type: 'rightAligned', width: 150, filter: 'agNumberColumnFilter', cellClass: 'numeric' },
  { headerName: 'APPROVALS AMOUNT', field: 'approvedAmount', type: 'rightAligned', width: 160, filter: 'agNumberColumnFilter', cellClass: 'numeric',
    headerTooltip: 'Amount of the approved, contracts and funded submissions', valueFormatter: (p) => money(Number(p.value) || 0) },
  { headerName: 'FUNDED UNITS', field: 'fundedCount', type: 'rightAligned', width: 135, filter: 'agNumberColumnFilter', cellClass: 'numeric funded-number' },
  { headerName: 'AMOUNT FUNDED', field: 'fundedAmount', type: 'rightAligned', width: 145, filter: 'agNumberColumnFilter', cellClass: 'numeric primary-cell', valueFormatter: (p) => money(Number(p.value) || 0) },
  { headerName: 'COMMISSIONS', field: 'grossCommission', type: 'rightAligned', width: 140, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => money(Number(p.value) || 0) },
  { headerName: '% RATIO OF MERCHANTS TO APPROVALS', field: 'merchantsToApprovalsPct', type: 'rightAligned', width: 250, filter: 'agNumberColumnFilter', cellClass: 'numeric',
    headerTooltip: 'Merchants with an approved, contracts or funded submission over all of the rep\'s merchants',
    valueFormatter: (p) => (p.value != null ? `${p.value}%` : '-') },
  { headerName: '% RATIO OF APPROVALS TO FUNDED', field: 'approvalToFundedPct', type: 'rightAligned', width: 240, filter: 'agNumberColumnFilter', cellClass: 'numeric',
    headerTooltip: 'Funded submissions over approved submissions',
    valueFormatter: (p) => (p.value != null ? `${p.value}%` : '-') },
  { headerName: '% RATIO OF MERCHANTS TO FUNDED', field: 'merchantsToFundedPct', type: 'rightAligned', width: 230, filter: 'agNumberColumnFilter', cellClass: 'numeric',
    headerTooltip: 'Merchants with a funded submission over all of the rep\'s merchants',
    valueFormatter: (p) => (p.value != null ? `${p.value}%` : '-') },
  { headerName: 'ACTIVE', colId: 'active', width: 100, filter: 'agSetColumnFilter',
    valueGetter: (p) => (p.data ? (p.data.active ? 'Yes' : 'No') : '') },
  { headerName: 'SUBMISSIONS', field: 'submissionCount', type: 'rightAligned', width: 140, filter: 'agNumberColumnFilter', cellClass: 'numeric', hide: true },
  { headerName: 'APPROVAL RATE', field: 'approvalRate', type: 'rightAligned', width: 140, filter: 'agNumberColumnFilter', cellClass: 'numeric', hide: true,
    valueFormatter: (p) => `${p.value}%` },
];

const SUB_ID_COLUMN: ColDef<SubmissionRecord> = {
  headerName: 'SUB ID', colId: 'subId', field: 'id', width: 100, filter: 'agNumberColumnFilter',
  valueFormatter: (p) => (p.value != null ? `#${p.value}` : '-'),
};
const MERCHANT_COLUMN: ColDef<SubmissionRecord> = { headerName: 'BUSINESS NAME', field: 'merchant', width: 200, cellClass: 'primary-cell', tooltipField: 'merchant' };
const STATUS_COLUMN: ColDef<SubmissionRecord> = { headerName: 'STATUS', colId: 'status', width: 150, filter: 'agSetColumnFilter',
  valueGetter: (p) => p.data ? STATUS_LABELS[p.data.decision] : '',
  cellRenderer: (p: { data?: SubmissionRecord }) => p.data ? <span className={`rep-status ${p.data.decision}`}><i />{STATUS_LABELS[p.data.decision]}</span> : null };
const AMOUNT_COLUMN: ColDef<SubmissionRecord> = { headerName: 'AMOUNT', field: 'amount', type: 'rightAligned', width: 120, filter: 'agNumberColumnFilter', cellClass: 'numeric primary-cell', valueFormatter: (p) => money(p.value) };
const FACTOR_COLUMN: ColDef<SubmissionRecord> = { headerName: 'FACTOR', field: 'factorRate', type: 'rightAligned', width: 100, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? Number(p.value).toFixed(2) : '-') };
const TERM_COLUMN: ColDef<SubmissionRecord> = { headerName: 'TERM', field: 'term', type: 'rightAligned', width: 90, filter: 'agNumberColumnFilter', cellClass: 'numeric', valueFormatter: (p) => (p.value != null ? String(p.value) : '-') };
const REASON_COLUMN: ColDef<SubmissionRecord> = { headerName: 'DECLINE REASON / NOTE', field: 'reason', width: 240, tooltipField: 'reason',
  cellClass: (p) => (p.data?.decision === 'declined' ? 'decline-reason' : ''), valueFormatter: (p) => p.value || '-' };
const FUNDER_COLUMN: ColDef<SubmissionRecord> = { headerName: 'FUNDER', field: 'funder', width: 170, tooltipField: 'funder' };
const FIRST_NAME_COLUMN: ColDef<SubmissionRecord> = { headerName: 'FIRST NAME', field: 'merchantFirstName', width: 130, valueFormatter: (p) => p.value || '-' };
const EMAIL_COLUMN: ColDef<SubmissionRecord> = { headerName: 'EMAIL', field: 'merchantEmail', width: 200, valueFormatter: (p) => p.value || '-' };
const hidden = (column: ColDef<SubmissionRecord>): ColDef<SubmissionRecord> => ({ ...column, hide: true });

// REG-828: the tabs follow the COLUMNS sheet of the matching list (SUBMISSIONS,
// FUNDED, APPROVALS); columns the sheet does not list stay hidden.
const SHEET = sheetDealColumns<SubmissionRecord>();
const REP_SUBMISSION_COLUMNS: ColDef<SubmissionRecord>[] = [
  SUB_ID_COLUMN,
  // REG-828: the Submissions list's Created At (long format); the old
  // activity date stays in the column chooser.
  SHEET.createdAt,
  SHEET.mainRep,
  MERCHANT_COLUMN,
  STATUS_COLUMN,
  REASON_COLUMN,
  AMOUNT_COLUMN,
  FACTOR_COLUMN,
  SHEET.payment,
  TERM_COLUMN,
  SHEET.commissionPct,
  SHEET.commission,
  SHEET.leadSource,
  SHEET.dealType,
  SHEET.brand,
  SHEET.contactMethod,
  hidden(FUNDER_COLUMN),
  { headerName: 'ACTIVITY DATE', field: 'date', width: 110, valueFormatter: (p) => shortDate(p.value), hide: true },
];

const FUNDED_TAB_COLUMNS: ColDef<SubmissionRecord>[] = [
  SUB_ID_COLUMN,
  { headerName: 'DATE FUNDED', field: 'dateFunded', width: 120, valueFormatter: (p) => shortDate(p.value) },
  SHEET.mainRep,
  MERCHANT_COLUMN,
  SHEET.performance,
  FIRST_NAME_COLUMN,
  SHEET.phone,
  EMAIL_COLUMN,
  AMOUNT_COLUMN,
  FACTOR_COLUMN,
  TERM_COLUMN,
  SHEET.payment,
  SHEET.commissionPct,
  SHEET.commission,
  SHEET.psf,
  SHEET.leadSource,
  SHEET.dealType,
  SHEET.brand,
  SHEET.contactMethod,
  hidden(FUNDER_COLUMN),
  hidden(STATUS_COLUMN),
  hidden(REASON_COLUMN),
];
const APPROVAL_TAB_COLUMNS: ColDef<SubmissionRecord>[] = [
  SUB_ID_COLUMN,
  { headerName: 'DATE APPROVED', field: 'approvalDate', width: 130, valueFormatter: (p) => shortDate(p.value) },
  SHEET.mainRep,
  MERCHANT_COLUMN,
  FIRST_NAME_COLUMN,
  SHEET.phone,
  EMAIL_COLUMN,
  SHEET.state,
  AMOUNT_COLUMN,
  FACTOR_COLUMN,
  TERM_COLUMN,
  SHEET.payment,
  SHEET.commissionPct,
  SHEET.commission,
  // REG-740: live dropdown for whoever holds edit_merchant_feedback (Openers
  // and Closers on their own deals); plain text otherwise.
  { headerName: 'MERCHANT FEEDBACK', colId: 'merchantResponse', field: 'merchantResponse', width: 180,
    filter: 'agSetColumnFilter', cellClass: 'live-picklist-cell',
    cellRenderer: (p: { data?: SubmissionRecord; value?: string | null }) => p.data ? (
      <LivePicklistCell
        target={MERCHANT_FEEDBACK_EDIT}
        recordId={p.data.merchantId}
        value={p.value ?? null}
        label="Merchant Feedback"
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['rep-performance-submissions'] })}
      />
    ) : null },
  SHEET.leadSource,
  SHEET.dealType,
  SHEET.brand,
  SHEET.contactMethod,
  hidden(FUNDER_COLUMN),
  hidden(STATUS_COLUMN),
  hidden(REASON_COLUMN),
];

type RepDetailTab = 'funded' | 'submissions' | 'approvals' | 'applications';
const REP_DETAIL_TABS: Array<{ id: RepDetailTab; label: string }> = [
  { id: 'funded', label: 'Funded' },
  { id: 'submissions', label: 'Submissions' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'applications', label: 'Applications' },
];

function SubmissionTable({ rows, onOpen, maxHeight, columns, rowHeight, rowActions, storageKey }: { rows: SubmissionRecord[]; onOpen: (row: SubmissionRecord) => void; maxHeight: number; columns?: ColDef<SubmissionRecord>[]; rowHeight?: number; rowActions?: GridRowAction<SubmissionRecord>[]; storageKey?: string }) {
  const { data: metadata } = useGlobalMetadata();
  const prefetchSubmission = useCallback((row: SubmissionRecord) => (
    prefetchRecordDestinationIfAbsent({ tableName: 'submission', id: row.id }, metadata)
  ), [metadata]);
  return (
    <WorkspaceGrid<SubmissionRecord>
      rows={rows}
      columns={columns ?? REP_SUBMISSION_COLUMNS}
      getRowId={(row) => String(row.id)}
      onRowClick={onOpen}
      onRowIntent={prefetchSubmission}
      height={maxHeight}
      rowHeight={rowHeight}
      rowActions={rowActions}
      storageKey={storageKey}
      emptyMessage="No submissions match these filters."
    />
  );
}

// The Send dialogs read report-grid row shapes (rpt_funded_deals_v /
// rpt_primary_approvals_v keys), so map SubmissionRecord onto that contract
// exactly as the main dashboard grids feed them (REG-663).
function offerDialogRow(row: SubmissionRecord): Record<string, unknown> {
  return {
    id: row.id,
    merchant: row.merchantId,
    email: row.merchantEmail,
    first_name: row.merchantFirstName,
    merchant_label: row.merchant,
    amount: row.amount,
    factor_rate: row.factorRate,
    term: row.term,
    structure: row.structure,
  };
}

function fundedDialogRow(row: SubmissionRecord): Record<string, unknown> {
  return {
    id: row.id,
    merchant: row.merchantId,
    email: row.merchantEmail,
    first_name: row.merchantFirstName,
    merchant_label: row.merchant,
    amount: row.amount,
    date_funded: row.dateFunded,
    funder_label: row.funder === 'Unassigned' ? '' : row.funder,
    main_rep: row.mainRep,
  };
}

function ChipEditButton({ onClick, label, title = 'Edit contact info' }: { onClick: () => void; label: string; title?: string }) {
  return (
    <button className="rep-chip-edit" onClick={onClick} title={title} aria-label={label}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
    </button>
  );
}

export function RepDetail({ rep, submissions, onBack, onActiveChange, onEditContact, onEditLogin, defaultAdditionalRepLabel, onEditDefaultAdditionalRep, closerLabel, onEditCloser }: { rep: RepRecord; submissions: SubmissionRecord[]; onBack?: () => void; onActiveChange?: (active: boolean) => Promise<void>; onEditContact?: () => void; onEditLogin?: () => void; defaultAdditionalRepLabel?: string; onEditDefaultAdditionalRep?: () => void; closerLabel?: string; onEditCloser?: () => void }) {
  const [savingActive, setSavingActive] = useState(false);
  // 460px is the floor; the grid grows to the bottom of the visible content
  // area so zooming out shows more rows (REG-652).
  const submissionsFill = useFillGridHeight(460);
  // Per-table zoom, remembered across sessions (REG-653).
  const [tableZoom, setTableZoom] = useTableZoom('rep_detail_submissions');
  const toggleActive = async (active: boolean) => {
    if (!onActiveChange || savingActive) return;
    setSavingActive(true);
    try {
      await onActiveChange(active);
      message.success(active ? `${rep.name} marked active.` : `${rep.name} marked inactive.`);
    } catch (error) {
      message.error((error as Error).message || 'Unable to update active status.');
    } finally {
      setSavingActive(false);
    }
  };
  const [status, setStatus] = useState('all');
  const [month, setMonth] = useState('all');
  const [year, setYear] = useState('all');
  const [amountBucket, setAmountBucket] = useState('all');
  const [reason, setReason] = useState('all');
  const [funderFilter, setFunderFilter] = useState('all');
  const [activeTab, setActiveTab] = useState<RepDetailTab>('funded');
  // Dev-only Funded Dashboard for just this rep (the same tables as Admin > Reports).
  const [showDashboard, setShowDashboard] = useState(false);
  const canSeeDashboard = useMyCapabilities().hasCapability('edit_permissions');
  const [activeSend, setActiveSend] = useState<{ kind: 'offer' | 'funded'; row: SubmissionRecord } | null>(null);
  const { openRecord } = useRecordNavigator();
  const years = [...new Set(submissions.filter((row) => row.date).map((row) => new Date(row.date!).getFullYear()))].sort((a, b) => b - a);
  const reasons = [...new Set(submissions.map((row) => row.declineReason).filter(Boolean))].sort();
  const funders = [...new Set(submissions.map((row) => row.funder).filter((value) => value !== 'Unassigned'))].sort();
  const bucket = AMOUNT_BUCKETS.find((item) => item.value === amountBucket) ?? AMOUNT_BUCKETS[0];
  const filtered = submissions.filter((row) => {
    const date = row.date ? new Date(row.date) : null;
    return (status === 'all' || row.decision === status)
      && (month === 'all' || date?.getMonth() === Number(month))
      && (year === 'all' || date?.getFullYear() === Number(year))
      && row.amount >= bucket.min && row.amount < bucket.max
      && (reason === 'all' || row.declineReason === reason)
      && (funderFilter === 'all' || row.funder === funderFilter);
  });
  const stats = statsFor(filtered);
  const allStats = statsFor(submissions);
  const rollups = funderRollups(submissions);
  const merchantCount = new Set(submissions.map((row) => row.merchantId).filter(Boolean)).size;
  const funderCount = funders.length;
  const filteredView = status !== 'all' || month !== 'all' || year !== 'all' || amountBucket !== 'all' || reason !== 'all' || funderFilter !== 'all';

  // Tab row sets, each ordered most recent first on the tab's own date.
  const fundedRows = filtered.filter((row) => row.decision === 'funded')
    .sort((a, b) => new Date(b.dateFunded ?? b.date ?? 0).getTime() - new Date(a.dateFunded ?? a.date ?? 0).getTime());
  const approvalRows = filtered.filter((row) => row.isPrimary)
    .sort((a, b) => new Date(b.approvalDate ?? b.date ?? 0).getTime() - new Date(a.approvalDate ?? a.date ?? 0).getTime());
  // One row per merchant: the latest submission stands in for the application.
  const applicationRows = (() => {
    const seen = new Set<number | string>();
    return filtered.filter((row) => {
      const id = row.merchantId ?? `s${row.id}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  })();
  const tabRows: Record<RepDetailTab, SubmissionRecord[]> = {
    funded: fundedRows,
    submissions: filtered,
    approvals: approvalRows,
    applications: applicationRows,
  };
  const tabColumns: Partial<Record<RepDetailTab, ColDef<SubmissionRecord>[]>> = {
    funded: FUNDED_TAB_COLUMNS,
    approvals: APPROVAL_TAB_COLUMNS,
  };
  // Per-row Send actions, mirroring the main dashboard grids (REG-663):
  // Funded rows send the "We Funded You" follow-up, Approval rows send the
  // funding-package offer.
  const tabRowActions: Partial<Record<RepDetailTab, GridRowAction<SubmissionRecord>[]>> = {
    funded: [{
      id: 'rep-send-funded-followup',
      label: 'Send',
      variant: 'button',
      ariaLabel: (row) => `Send funded follow-up for ${row.merchant}`,
      onSelect: (row) => setActiveSend({ kind: 'funded', row }),
    }],
    approvals: [{
      id: 'rep-send-offer',
      label: 'Send',
      variant: 'button',
      ariaLabel: (row) => `Send funding package for ${row.merchant}`,
      onSelect: (row) => setActiveSend({ kind: 'offer', row }),
    }],
  };

  const openSubmission = (row: SubmissionRecord) => openRecord({ id: row.id, tableName: 'submission', label: row.merchant });
  const clearFilters = () => { setStatus('all'); setMonth('all'); setYear('all'); setAmountBucket('all'); setReason('all'); setFunderFilter('all'); };

  return <div className="rep-detail" data-testid="record-detail">
    {onBack && <button className="rep-back" onClick={onBack}>‹ <span>All reps</span></button>}
    <header className="rep-detail-header">
      <span className="rep-detail-avatar">{initials(rep.name)}</span>
      <div className="rep-detail-title"><h1>{rep.name}</h1><p>{rep.active ? 'Sales representative' : 'Inactive'}</p></div>
      <div className="rep-header-chips">
        <div className="rep-contact-chip">
          <small>EMAIL{onEditContact && <ChipEditButton onClick={onEditContact} label={`Edit email for ${rep.name}`} />}</small>
          <b title={rep.email || undefined}>{rep.email || '-'}</b>
        </div>
        <div className="rep-contact-chip">
          <small>PHONE{onEditContact && <ChipEditButton onClick={onEditContact} label={`Edit phone for ${rep.name}`} />}</small>
          <b>{rep.phone || '-'}</b>
        </div>
        <div className="rep-contact-chip">
          <small>LOGIN{onEditLogin && <ChipEditButton onClick={onEditLogin} label={`Link a login to ${rep.name}`} title="Link a CRM login" />}</small>
          <b title={rep.loginId == null ? 'No CRM login is linked, so this rep cannot see their own deals' : undefined}>
            {rep.loginId == null ? 'Not linked' : rep.loginLabel || `Login #${rep.loginId}`}
          </b>
        </div>
        {/* REG-876: admin-only; a rep's own dashboard does not show it. */}
        {onEditDefaultAdditionalRep && (
          <div className="rep-contact-chip" data-testid="rep-default-additional-rep">
            <small>DEFAULT ADDITIONAL REP<ChipEditButton onClick={onEditDefaultAdditionalRep} label={`Edit default additional rep for ${rep.name}`} title="Added as an additional rep on this rep's new merchants" /></small>
            <b title="Added as an additional rep (visibility only) on this rep's new merchants">
              {rep.defaultAdditionalRepId == null ? 'None' : defaultAdditionalRepLabel || `Rep #${rep.defaultAdditionalRepId}`}
            </b>
          </div>
        )}
        {/* REG-859: admin-only, like the default additional rep. */}
        {onEditCloser && (
          <div className="rep-contact-chip" data-testid="rep-closer">
            <small>CLOSER<ChipEditButton onClick={onEditCloser} label={`Edit closer for ${rep.name}`} title="The closer assigned to this rep" /></small>
            <b title="Copied on this rep's deal preps and funder emails, and added as an additional rep when their deals fund">
              {rep.closerId == null ? 'None' : closerLabel || `Rep #${rep.closerId}`}
            </b>
          </div>
        )}
        <div className="rep-active-chip">
          <small>ACTIVE</small>
          <label title={onActiveChange ? `Mark ${rep.name} ${rep.active ? 'inactive' : 'active'}` : 'Read-only'}>
            <input
              type="checkbox"
              checked={rep.active}
              disabled={!onActiveChange || savingActive}
              onChange={(event) => void toggleActive(event.target.checked)}
            />
            <b className={rep.active ? 'accent' : ''}>{savingActive ? 'Saving…' : rep.active ? 'Yes' : 'No'}</b>
          </label>
        </div>
        <ApprovalTextChip repId={rep.id} />
        <div><small>HIRED</small><b>{shortDate(rep.createdAt)}</b></div><div><small>TENURE</small><b className="accent">{tenure(rep.createdAt)}</b></div>
        <div><small>MERCHANTS</small><b>{merchantCount}</b></div><div><small>FUNDERS USED</small><b>{funderCount}</b></div><div><small>LAST SUBMISSION</small><b>{shortDate(allStats.lastDate)}</b></div>
      </div>
    </header>
    <div className="rep-filter-bar"><span className="rep-filter-label">☰ &nbsp; FILTERS</span>
      <Select label="Status" value={status} onChange={setStatus}><option value="all">All statuses</option><option value="funded">Funded</option><option value="approved">Approved</option><option value="declined">Declined</option><option value="no_response">No Response</option></Select>
      <Select label="Funder" value={funderFilter} onChange={setFunderFilter}><option value="all">All funders</option>{funders.map((value) => <option key={value}>{value}</option>)}</Select>
      <Select label="Month" value={month} onChange={setMonth}><option value="all">All months</option>{MONTHS.map((label, index) => <option key={label} value={index}>{label}</option>)}</Select>
      <Select label="Year" value={year} onChange={setYear}><option value="all">All years</option>{years.map((value) => <option key={value}>{value}</option>)}</Select>
      <Select label="Amount" value={amountBucket} onChange={setAmountBucket}>{AMOUNT_BUCKETS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</Select>
      <Select label="Decline reason" value={reason} onChange={setReason}><option value="all">Any decline reason</option>{reasons.map((value) => <option key={value}>{value}</option>)}</Select>
      {filteredView && <button onClick={clearFilters}>Clear</button>}<span className="filter-count">Showing <b>{stats.total}</b> of {submissions.length} submissions</span>
    </div>
    <div className="rep-stat-grid primary">
      <StatCard large label="Primary Approvals" value={stats.primaryApprovals} sub="one per merchant" accent="var(--accent)" delta={monthDelta(submissions, 'primary')} />
      <StatCard large label="Applications" value={stats.applications} sub="individual merchants" delta={monthDelta(submissions, 'applications')} />
      <StatCard large label="Approval Amount" value={shortMoney(stats.approvalAmount)} sub={money(stats.approvalAmount)} accent="var(--accent)" delta={monthDelta(submissions, 'approvalAmount', true)} />
      <StatCard large label="Units Funded" value={stats.fundedUnits} sub={`${stats.fundedMerchants} merchants`} delta={monthDelta(submissions, 'fundedUnits')} />
      <StatCard large label="Amount Funded" value={shortMoney(stats.fundedAmount)} sub={money(stats.fundedAmount)} accent="var(--green)" delta={monthDelta(submissions, 'fundedAmount', true)} />
    </div>
    <div className="rep-stat-grid secondary">
      <StatCard label="Avg Deal Size" value={shortMoney(stats.averageDeal)} sub="per funded deal" />
      <RateCard label="Approval Rate" value={stats.approvalRate} sub={`${stats.approvedUnits} / ${stats.applications} applications`} accent="var(--green)" />
      <RateCard label="Funded Rate" value={stats.fundedRate} sub={`${stats.fundedUnits} / ${stats.approvedUnits} approved`} accent="var(--accent)" />
      <FavoriteFunderCard favorite={rollups[0]} />
    </div>
    <div className="rep-two-col">
      <MonthlyFundedChart submissions={submissions} />
      <TopFundersCard rollups={rollups} />
    </div>
    <section className="rep-data-card">
      <div className="rep-tabs" role="tablist">
        {REP_DETAIL_TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={!showDashboard && activeTab === tab.id}
            className={!showDashboard && activeTab === tab.id ? 'active' : ''}
            onClick={() => { setActiveTab(tab.id); setShowDashboard(false); }}
          >
            {tab.label} <em>{tabRows[tab.id].length}</em>
          </button>
        ))}
        {canSeeDashboard && <button role="tab" aria-selected={showDashboard} className={showDashboard ? 'active' : ''} onClick={() => setShowDashboard(true)}>Dashboard</button>}
        <span className="rep-tabs-note">{filteredView ? 'filtered view' : 'all time'}</span>
        <GridZoomControl zoom={tableZoom} onChange={setTableZoom} />
      </div>
      {showDashboard ? <FundedDashboard embedded scope={{ kind: 'rep', id: rep.id }} /> : <div ref={submissionsFill.ref} style={{ ['--sky-grid-zoom' as string]: tableZoom }}>
        <SubmissionTable
          // One grid per tab, so each restores its own saved columns.
          key={activeTab}
          storageKey={`rep_detail_${activeTab}`}
          rows={tabRows[activeTab]}
          onOpen={openSubmission}
          maxHeight={submissionsFill.height}
          columns={tabColumns[activeTab]}
          rowHeight={Math.round(40 * tableZoom)}
          rowActions={tabRowActions[activeTab]}
        />
      </div>}
    </section>
    <SendOfferDialog
      row={activeSend?.kind === 'offer' ? offerDialogRow(activeSend.row) : null}
      onClose={() => setActiveSend(null)}
    />
    <SendFundedDialog
      row={activeSend?.kind === 'funded' ? fundedDialogRow(activeSend.row) : null}
      onClose={() => setActiveSend(null)}
    />
  </div>;
}

export default function RepPage(): React.ReactElement {
  const { openRecord, closeRecord } = useRecordNavigator();
  const selectedId = useRecordParam('rep');
  const repsQuery = useQuery({ queryKey: ['rep-performance-page'], queryFn: loadRepPageData, staleTime: 5 * 60_000 });
  const { data: reps, isLoading, error } = repsQuery;
  const { isSuperAdmin, hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
  // Mirrors the RLS gate: rep UPDATE requires the edit_deals capability.
  const canEditReps = !capabilitiesLoading && hasCapability('edit_deals');
  // Mirrors core_crm.set_rep_login: linking logins is user management.
  const canLinkLogins = !capabilitiesLoading && (isSuperAdmin || hasCapability('manage_users') || hasCapability('edit_permissions'));
  const [contactEditRep, setContactEditRep] = useState<RepRecord | null>(null);
  const [loginEditRep, setLoginEditRep] = useState<RepRecord | null>(null);
  const [defaultAdditionalEditRep, setDefaultAdditionalEditRep] = useState<RepRecord | null>(null);
  const [closerEditRep, setCloserEditRep] = useState<RepRecord | null>(null);
  const selected = selectedId ? reps?.find((rep) => rep.id === selectedId) : null;
  const detailQuery = useQuery({
    queryKey: ['rep-performance-submissions', selectedId],
    queryFn: () => fetchRepSubmissions(selectedId!),
    enabled: selectedId != null,
    staleTime: 2 * 60_000,
  });
  if (isLoading || !reps) return <LoadingState error={error as Error | null} />;
  if (selectedId && !selected) return <LoadingState error={new Error('This rep no longer exists.')} />;
  const setRepActive = async (repId: number, active: boolean) => {
    const { data, error: updateError } = await (supabaseClient as any)
      .from('rep')
      .update({ is_active: active })
      .eq('id', repId)
      .select('id');
    if (updateError) throw updateError;
    if (!data?.length) throw new Error('You may not have permission to change rep status.');
    await repsQuery.refetch();
  };
  const contactDialog = contactEditRep ? (
    <RepContactDialog
      rep={contactEditRep}
      onClose={() => setContactEditRep(null)}
      onSaved={() => repsQuery.refetch()}
    />
  ) : null;
  if (selected) {
    if (detailQuery.isLoading || !detailQuery.data) return <LoadingState error={detailQuery.error as Error | null} />;
    return (
      <>
        <RepDetail
          rep={selected}
          submissions={detailQuery.data}
          onBack={closeRecord}
          onActiveChange={canEditReps ? (active) => setRepActive(selected.id, active) : undefined}
          onEditContact={canEditReps ? () => setContactEditRep(selected) : undefined}
          onEditLogin={canLinkLogins ? () => setLoginEditRep(selected) : undefined}
          defaultAdditionalRepLabel={reps.find((other) => other.id === selected.defaultAdditionalRepId)?.name}
          onEditDefaultAdditionalRep={canEditReps ? () => setDefaultAdditionalEditRep(selected) : undefined}
          closerLabel={reps.find((other) => other.id === selected.closerId)?.name}
          onEditCloser={canEditReps ? () => setCloserEditRep(selected) : undefined}
        />
        {contactDialog}
        {defaultAdditionalEditRep ? (
          <RepDefaultAdditionalRepDialog
            rep={defaultAdditionalEditRep}
            reps={reps}
            onClose={() => setDefaultAdditionalEditRep(null)}
            onSaved={() => repsQuery.refetch()}
          />
        ) : null}
        {closerEditRep ? (
          <RepCloserDialog
            rep={closerEditRep}
            reps={reps}
            onClose={() => setCloserEditRep(null)}
            onSaved={() => repsQuery.refetch()}
          />
        ) : null}
        {loginEditRep ? (
          <RepLoginLinkDialog
            target={{ kind: 'rep', id: loginEditRep.id, label: loginEditRep.name }}
            onClose={() => setLoginEditRep(null)}
            onSaved={() => repsQuery.refetch()}
          />
        ) : null}
      </>
    );
  }
  return (
    <>
      <RepList
        reps={reps}
        onOpen={(id) => openRecord({ tableName: 'rep', id })}
        onEditContact={canEditReps ? setContactEditRep : undefined}
        onToggleActive={canEditReps ? (rep, active) => setRepActive(rep.id, active) : undefined}
        createAction={(
          <CreateListRecordButton
            tableName="rep"
            onCreated={async (created) => {
              await repsQuery.refetch();
              openRecord({ tableName: 'rep', id: created.id, label: created.label });
            }}
          />
        )}
      />
      {contactDialog}
    </>
  );
}
